<template>
    <PageWrapper :breadcrumb-links="breadcrumbLinks">
        <main
            class="mx-auto w-full max-w-7xl px-2 lg:pl-[calc(theme(spacing.64)+theme(spacing.4))]"
        >
            <StickyPanel
                :top-default="0"
                :top-responsive="10"
            >
                <FilterToolbar
                    :key="`template-filter-toolbar-` + pager.key"
                    :page-data="pageData"
                    :buttons="toolbarConfig.buttons"
                />
                <OrderBar
                    :current="pageData.sortBy"
                    :order-options="pageData.sortOptions"
                />
            </StickyPanel>
            <BatchImportSteps :steps="wizzardSteps" />
            <AlertBox
                v-if="pageData.data.items.length === 0 && pageData.loading === false"
                state="warning"
                :icon="MdiInformation"
                :title="t('Templates')"
            >
                <p class="py-1 text-sm">{{ t('notFoundText') }}</p>
            </AlertBox>
            <div
                v-if="pageData.data.items.length > 0"
                :key="`page-data-list-${pageDataListKey}`"
                class="divide-y divide-gray-200"
            >
                <div
                    v-for="template in pageData.data.items"
                    :key="template.response.templateId"
                    class="group/template"
                >
                    <div
                        class="flex cursor-pointer items-center justify-start gap-4 py-2 transition duration-150 ease-in-out hover:bg-gray-100"
                        @click="nextStep(template.response)"
                    >
                        <div class="flex-none px-2">
                            <MdiFilePdfBox
                                v-if="template.response.type == 'PDF'"
                                class="h-7 w-7 text-rose-400"
                            />
                            <MdiEmail
                                v-if="template.response.type == 'EMAIL'"
                                class="h-7 w-7 text-sky-400"
                            />
                            <MdiCellphoneText
                                v-if="template.response.type == 'SMS'"
                                class="h-7 w-7 text-green-400"
                            />
                            <MdiInbox
                                v-if="template.response.type == 'INBOX'"
                                class="h-7 w-7 text-amber-400"
                            />
                        </div>
                        <div class="flex grow flex-col gap-2">
                            <p class="truncate text-sm font-medium text-wrap text-blue-600">
                                {{ template.response.name }}
                            </p>
                            <p class="truncate text-xs text-wrap break-all text-gray-500">
                                {{ template.response.code }}
                            </p>
                            <div
                                class="flex flex-row items-start text-sm text-gray-500 select-none"
                            >
                                <TagShow :tags="template.extension.TAGS"></TagShow>
                            </div>
                        </div>
                        <div class="hidden md:flex md:flex-col md:items-end md:justify-start">
                            <AuditInfo :item="template.response" />
                        </div>
                        <div class="flex-none px-2">
                            <MdiChevronRight class="h-5 w-5 text-gray-400" />
                        </div>
                    </div>
                </div>
            </div>
        </main>
    </PageWrapper>
    <div
        class="fixed right-0 bottom-0 left-1 z-2000 flex h-16 items-center justify-center bg-white lg:left-64"
    >
        <Pager
            :key="pager.key"
            :current="pager.current"
            :end="pager.end"
        />
    </div>
    <Spinner
        v-if="pageData.loading === true"
        type="dots"
        :color="`text-blue-600`"
        :color2="`text-blue-400`"
        :text-color="`text-gray-200`"
        :overlay="true"
        :speed="1000"
        :dots="true"
        :dot-char="'.'"
        :dot-dual="false"
        :dot-reverse="false"
        :text="t('loading')"
        :inline="false"
        :pulse="true"
    />
</template>

<script setup>
import { reactive, watch, inject, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getTemplates } from '../composables/useTemplates';
import { t } from '../composables/i18n';
import { listTags } from '../composables/useTags';
import BatchImportSteps from '../components/BatchImportSteps.vue';
import TagShow from '../ui/TagShow.vue';
import AlertBox from '../ui/AlertBox.vue';
import AuditInfo from '../ui/AuditInfo.vue';
import Spinner from '../ui/Spinner.vue';
import FilterToolbar from '../ui/FilterToolbar.vue';
import OrderBar from '../ui/OrderBar.vue';
import Pager from '../ui/Pager.vue';
import { makePager } from '../composables/usePaginator';
import PageWrapper from '../ui/PageWrapper.vue';
import StickyPanel from '../ui/StickyPanel.vue';
import { reloadPageData } from '../composables/usePages';
import { parseSortBy, sortByChanged } from '../composables/useSortBy';
import { getRndKey } from '../composables/utils';
import {
    MdiChevronRight,
    MdiFilePdfBox,
    MdiEmail,
    MdiCellphoneText,
    MdiInbox,
    MdiInformation,
} from 'materialdesignicons-vue3/icons/';

const route = useRoute();
const router = useRouter();
const config = inject('config');
const sortDefault = ['name:asc'];
const pageDataListKey = ref(getRndKey(17));
const pageData = reactive({
    data: {
        items: [],
        totalCount: 0,
    },
    currentPage: parseInt(route.params.page) || 1,
    perPage: 15,
    search: route.query.search || '',
    type: route.query.type || '',
    loading: true,
    useTags: true,
    tags: {
        items: [],
        totalCount: 0,
    },
    selectedTags: [],
    sortDefault,
    sortBy: parseSortBy(route.query.sortBy, sortDefault),
    filters: {
        type: {
            // active value
            active: route.query.type || '',
            name: t('filterType'),
            values: [
                {
                    value: '',
                    name: t('filterAll'),
                },
                ...config.templates.allowTypes.map((type) => {
                    return {
                        value: type,
                        name: type,
                    };
                }),
            ],
        },
    },
    sortOptions: [
        /* { name: 'Id', value: 'id' }, */
        { name: t('sortName'), value: 'name' },
        { name: t('sortCode'), value: 'code' },
        { name: t('sortCreated'), value: 'created' },
        { name: t('sortUpdated'), value: 'updated' },
    ],
});

const pager = reactive(makePager(pageData.currentPage, pageData.data.totalCount, pageData.perPage));

const breadcrumbLinks = reactive([
    {
        name: 'dashboard',
        title: t('Dashboard'),
    },
    {
        name: 'templates',
        title: t('Templates'),
        final: true,
    },
]);

const refreshPageData = async () => {
    await reloadPageData(pageData, getTemplates, pager);
    breadcrumbLinks[1].title = `${t('Templates')} (${pageData.data.totalCount})`;
    pageDataListKey.value = getRndKey(17);
};

watch(
    () => route.params.page,
    async (newPage, oldPage) => {
        if (newPage !== oldPage) {
            //console.log('Route page changed', newPage, oldPage);
            pageData.currentPage = parseInt(newPage) || 1;
            await refreshPageData();
        }
    },
);

watch(
    () => route.query,
    async (newQuery, oldQuery) => {
        const filterKeys = Object.keys(pageData.filters);
        const searchChanged = newQuery.search !== oldQuery.search;
        const filtersChanged = filterKeys.some((key) => newQuery[key] !== oldQuery[key]);
        const tagsChanged = JSON.stringify(newQuery.tag) !== JSON.stringify(oldQuery.tag);
        const sortChanged = sortByChanged(newQuery, oldQuery);
        if (searchChanged || filtersChanged || tagsChanged || sortChanged) {
            //console.log('Query changed', newQuery, oldQuery);
            pageData.search = newQuery.search || '';
            for (const key of filterKeys) {
                pageData.filters[key].active = newQuery[key] || '';
            }
            if (tagsChanged) {
                pageData.selectedTags = newQuery.tag
                    ? Array.isArray(newQuery.tag)
                        ? newQuery.tag
                        : [newQuery.tag]
                    : [];
            }
            if (sortChanged) {
                pageData.sortBy = parseSortBy(newQuery.sortBy, pageData.sortDefault);
            }
            pageData.currentPage = 1;
            await refreshPageData();
        }
    },
);

onMounted(async () => {
    if (route.query.tag) {
        pageData.selectedTags = Array.isArray(route.query.tag)
            ? route.query.tag
            : [route.query.tag];
    }
    //console.log('Selected tags on mount:', pageData.selectedTags);
    pageData.tags = (await listTags({ isValid: true })) || {
        items: [],
        totalCount: 0,
    };
    await refreshPageData();
});

const nextStep = (template) => {
    router.push({
        name: 'batch-import-2',
        params: {
            templateid: template.templateId,
        },
    });
};

const toolbarConfig = reactive({
    buttons: [],
});

const wizzardSteps = [
    {
        id: '01',
        title: t('Templates'),
        status: 'current',
    },
    {
        id: '02',
        title: t('TemplateVersions'),
        status: 'upcoming',
    },
    {
        id: '03',
        title: t('buttonSchema'),
        status: 'upcoming',
    },
    {
        id: '04',
        title: t('processFiles'),
        status: 'upcoming',
    },
];
</script>
